<template>
  <div class="area-product-image-page">
    <!-- 顶部工具栏 -->
    <div class="top-toolbar">
      <!-- 标题 -->
      <div class="page-title">区域平面图</div>
      
      <!-- 图片指示器 -->
      <div v-if="imageList.length > 1" class="image-indicator">
        <span class="indicator-text">{{ currentIndex + 1 }} / {{ imageList.length }}</span>
      </div>
      
      <v-spacer />
    </div>

    <!-- 图片容器 -->
    <div 
      class="image-container d-flex align-center justify-center" 
      @wheel.prevent="handleWheel"
    >
      <div v-if="!currentImageUrl" class="no-image-placeholder">
        <v-icon icon="mdi-image-off" size="100" color="grey" />
        <div class="mt-4">暂无区域平面图</div>
      </div>
      <img
        v-else
        :src="currentImageUrl"
        class="preview-image"
        :style="{
          transform: `translate(${dragOffset.x}px, ${dragOffset.y}px) scale(${scale}) rotate(${rotation}deg)`,
        }"
        draggable="false"
        @mousedown="startDrag"
        @mousemove="onDrag"
        @mouseup="stopDrag"
        @mouseleave="stopDrag"
        @click.stop
        @error="handleImageError"
      >
    </div>

    <!-- 左右切换按钮 -->
    <v-btn
      v-if="imageList.length > 1"
      icon="mdi-chevron-left"
      variant="text"
      size="large"
      class="nav-btn prev-btn"
      @click="prevImage"
      :disabled="currentIndex === 0"
    />
    <v-btn
      v-if="imageList.length > 1"
      icon="mdi-chevron-right"
      variant="text"
      size="large"
      class="nav-btn next-btn"
      @click="nextImage"
      :disabled="currentIndex === imageList.length - 1"
    />

    <!-- 底部工具栏 -->
    <div class="bottom-toolbar">
      <!-- 缩放控制 -->
      <v-btn
        icon="mdi-minus"
        variant="text"
        size="large"
        @click="zoomOut"
        :disabled="scale <= 0.5"
        title="缩小"
      />
      <span class="zoom-text">{{ Math.round(scale * 100) }}%</span>
      <v-btn
        icon="mdi-plus"
        variant="text"
        size="large"
        @click="zoomIn"
        :disabled="scale >= 3"
        title="放大"
      />
      
      <v-divider vertical class="mx-2" />
      
      <!-- 旋转控制 -->
      <v-btn
        icon="mdi-rotate-left"
        variant="text"
        size="large"
        @click="rotateLeft"
        title="左旋转"
      />
      <v-btn
        icon="mdi-rotate-right"
        variant="text"
        size="large"
        @click="rotateRight"
        title="右旋转"
      />
      
      <v-divider vertical class="mx-2" />
      
      <!-- 重置 -->
      <v-btn
        icon="mdi-restore"
        variant="text"
        size="large"
        @click="resetImageState"
        title="重置"
      />
      
      <v-divider vertical class="mx-2" />
      
      <!-- 下载 -->
      <v-btn
        icon="mdi-download"
        variant="text"
        size="large"
        @click="handleDownload"
        :disabled="!currentImageUrl"
        title="下载"
      />
      
      <v-divider vertical class="mx-2" />
      
      <!-- 删除 -->
      <v-btn
        icon="mdi-delete"
        variant="text"
        size="large"
        color="error"
        @click="handleDelete"
        :disabled="imageList.length === 0"
        title="删除"
      />
    </div>

    <!-- 删除确认对话框 -->
    <v-dialog
      v-model="showDeleteConfirm"
      max-width="400"
      :scrim="false"
    >
      <v-card class="delete-confirm-dialog">
        <v-card-title class="text-h6">
          确认删除
        </v-card-title>
        <v-card-text>
          确定要删除这张区域平面图吗？
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="error"
            variant="tonal"
            @click="showDeleteConfirm = false"
          >
            取消
          </v-btn>
          <v-btn
            color="primary"
            variant="flat"
            @click="confirmDelete"
            :loading="deleting"
          >
            确定
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- 加载提示 -->
    <v-overlay
      v-model="loading"
      class="align-center justify-center"
    >
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { contractApi } from '@/services/api'
import { useSnackbar } from '@/composables/useSnackbar'

export default defineComponent({
  name: 'AreaProductImage',
  setup() {
    // 路由和工具
    const router = useRouter()
    const route = useRoute()
    const { showSuccess, showError } = useSnackbar()

    // 状态管理
    const loading = ref(false)
    const deleting = ref(false)
    const imageList = ref<string[]>([])
    const currentIndex = ref(0)
    const areaData = ref<any>({})
    // 消息提示
    const snackbar = ref({
      show: false,
      message: '',
      color: 'success'
    })

    // 图片状态
    const scale = ref(1)
    const rotation = ref(0)
    const isDragging = ref(false)
    const dragStart = ref({ x: 0, y: 0 })
    const dragOffset = ref({ x: 0, y: 0 })

    // 当前图片URL
    const currentImageUrl = computed(() => {
      if (imageList.value && imageList.value.length > 0) {
        return imageList.value[currentIndex.value] || ''
      }
      return ''
    })

    const contractId = ref('')
    const areaIndex = ref(0)
    // 初始化数据
    onMounted(() => {
        contractId.value = route.params.contractId as string
        areaIndex.value = Number(route.params.index as string)
        console.log('contractId', contractId.value)
        console.log('areaIndex', areaIndex.value)
        loadAreaImages()
    })

    // 加载区域图片
    const loadAreaImages = async () => {
        loading.value = true
        try{
            const res = (await contractApi.getAreaSummary({
                contractId : Number(contractId.value)
            }))
            if (res?.code === 0) {
                let data = res.data.find((item: any) => item.id === areaIndex.value)
                areaData.value = data
                if(data.imageUrls){
                    let imgList = JSON.parse(data.imageUrls)
                    if(imgList){
                        for(let i = 0; i < imgList.length; i++){
                            imageList.value.push(imgList[i].url)
                        }
                    }
                }
            }else{
                showError(res?.message)
            }
        }catch(error){
            showError(error.message || error.msg)
        }finally {
            loading.value = false
        }
    }

    // 图片切换
    const nextImage = () => {
      if (currentIndex.value < imageList.value.length - 1) {
        currentIndex.value++
        resetImageState()
      }
    }

    const prevImage = () => {
      if (currentIndex.value > 0) {
        currentIndex.value--
        resetImageState()
      }
    }

    // 重置图片状态
    const resetImageState = () => {
      scale.value = 1
      rotation.value = 0
      dragOffset.value = { x: 0, y: 0 }
    }

    // 缩放功能
    const zoomIn = () => {
      if (scale.value < 3) {
        scale.value = Math.min(3, scale.value + 0.2)
      }
    }

    const zoomOut = () => {
      if (scale.value > 0.5) {
        scale.value = Math.max(0.5, scale.value - 0.2)
      }
    }

    const handleWheel = (e: WheelEvent) => {
      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    }

    // 旋转功能
    const rotateLeft = () => {
      rotation.value = (rotation.value - 90) % 360
    }

    const rotateRight = () => {
      rotation.value = (rotation.value + 90) % 360
    }

    // 拖动功能
    const startDrag = (e: MouseEvent) => {
      isDragging.value = true
      dragStart.value = {
        x: e.clientX - dragOffset.value.x,
        y: e.clientY - dragOffset.value.y
      }
    }

    const onDrag = (e: MouseEvent) => {
      if (isDragging.value) {
        dragOffset.value = {
          x: e.clientX - dragStart.value.x,
          y: e.clientY - dragStart.value.y
        }
      }
    }

    const stopDrag = () => {
      isDragging.value = false
    }

    // 下载功能
    const handleDownload = async () => {
      if (!currentImageUrl.value) return
      
      try {
        const response = await fetch(currentImageUrl.value)
        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)
        
        const link = document.createElement('a')
        link.href = blobUrl
        link.download = `area_image_${currentIndex.value + 1}.${getFileExtension(currentImageUrl.value)}`
        
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
        
        window.URL.revokeObjectURL(blobUrl)
        showMessage('下载成功', 'success')
      } catch (error) {
        console.error('下载图片失败:', error)
        // 降级方案：直接打开
        window.open(currentImageUrl.value, '_blank')
      }
    }

    // 获取文件扩展名
    const getFileExtension = (url: string) => {
      const match = url.match(/\.([^.?]+)(\?|$)/)
      return match ? match[1] : 'jpg'
    }

    // 删除功能
    const showDeleteConfirm = ref(false)
    
    const handleDelete = () => {
      if (imageList.value.length > 0) {
        showDeleteConfirm.value = true
      }
    }

    const confirmDelete = async () => {
      deleting.value = true
      try {
        const res = await contractApi.deleteAreaImage({
            contractId : Number(contractId.value),
            building: areaData.value.building,
            roomType: areaData.value.roomType,
            floor: areaData.value.floor,
            areaRoomType: areaData.value.areaRoomType,
            imageUrl: currentImageUrl.value,
        }) 
        console.log('res', res)
        if (res?.code === 0) {
            // 从列表中删除
            imageList.value.splice(currentIndex.value, 1)
            if (imageList.value.length === 0) {
            //   showMessage('删除成功，即将返回', 'success')
            } else {
                // 调整当前索引
                if (currentIndex.value >= imageList.value.length) {
                    currentIndex.value = imageList.value.length - 1
                }
                resetImageState()
            }
            showSuccess('删除成功')
        }else{
            showError(res?.message)
        }
      } catch (error) {
        console.error('删除失败:', error)
        showMessage('删除失败', 'error')
      } finally {
        deleting.value = false
        showDeleteConfirm.value = false
      }
    }

    // 图片加载错误处理
    const handleImageError = () => {
      showMessage('图片加载失败', 'error')
    }

    // 键盘导航
    const handleKeydown = (e: KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (imageList.value.length > 1) {
            prevImage()
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (imageList.value.length > 1) {
            nextImage()
          }
          break
        case '+':
        case '=':
          e.preventDefault()
          zoomIn()
          break
        case '-':
          e.preventDefault()
          zoomOut()
          break
      }
    }

    // 显示消息
    const showMessage = (message: string, color: string = 'success') => {
      snackbar.value = {
        show: true,
        message,
        color
      }
    }

    // 组件卸载
    onUnmounted(() => {
      document.removeEventListener('keydown', handleKeydown)
    })

    return {
      // 数据
      loading,
      deleting,
      imageList,
      currentIndex,
      currentImageUrl,
      
      // 图片状态
      scale,
      rotation,
      dragOffset,
      
      // 对话框
      showDeleteConfirm,
      
      // 消息
      snackbar,
      
      // 方法
      nextImage,
      prevImage,
      resetImageState,
      zoomIn,
      zoomOut,
      handleWheel,
      rotateLeft,
      rotateRight,
      handleDownload,
      startDrag,
      onDrag,
      stopDrag,
      handleDelete,
      confirmDelete,
      handleImageError
    }
  }
})
</script>

<style lang="scss" scoped>
.area-product-image-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.95);
  z-index: 2000;
  display: flex;
  flex-direction: column;
}

/* 顶部工具栏 */
.top-toolbar {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 64px;
  display: flex;
  align-items: center;
  padding: 0 16px;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
  z-index: 100;
}

.back-btn {
  color: white;
}

.page-title {
  color: white;
  font-size: 20px;
  font-weight: 500;
  margin-left: 16px;
}

.image-indicator {
  background: rgba(255, 255, 255, 0.15);
  padding: 8px 16px;
  border-radius: 20px;
  margin-left: 24px;
}

.indicator-text {
  color: white;
  font-size: 16px;
  font-weight: 500;
}

/* 图片容器 */
.image-container {
  flex: 1;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  margin-top: 64px;
  margin-bottom: 80px;
}

.no-image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.5);
  font-size: 18px;
}

.preview-image {
  max-width: 90vw;
  max-height: calc(100vh - 200px);
  width: auto;
  height: auto;
  object-fit: contain;
  cursor: grab;
  transition: transform 0.2s ease;
  transform-origin: center;
  user-select: none;
}

.preview-image:active {
  cursor: grabbing;
}

/* 导航按钮 */
.nav-btn {
  position: fixed;
  top: 50%;
  transform: translateY(-50%);
  z-index: 100;
  color: white !important;
  background: rgba(0, 0, 0, 0.4) !important;
  border-radius: 50%;
  width: 64px !important;
  height: 64px !important;
}

.nav-btn:hover {
  background: rgba(0, 0, 0, 0.6) !important;
}

.nav-btn:disabled {
  color: rgba(255, 255, 255, 0.3) !important;
  background: rgba(0, 0, 0, 0.2) !important;
}

.prev-btn {
  left: 32px;
}

.next-btn {
  right: 32px;
}

/* 底部工具栏 */
.bottom-toolbar {
  position: fixed;
  bottom: 24px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 4px;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(10px);
  padding: 12px 24px;
  border-radius: 40px;
  z-index: 100;
}

.bottom-toolbar .v-btn {
  color: white !important;
}

.bottom-toolbar .v-btn:disabled {
  color: rgba(255, 255, 255, 0.3) !important;
}

.zoom-text {
  color: white;
  font-size: 14px;
  font-weight: 500;
  min-width: 50px;
  text-align: center;
}

/* 删除确认对话框 */
.delete-confirm-dialog {
  background-color: #2B2D33 !important;
}

.delete-confirm-dialog :deep(.v-card-title) {
  color: #fff;
  border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-card-text) {
  color: rgba(255, 255, 255, 0.7);
  padding: 16px;
}

.delete-confirm-dialog :deep(.v-card-actions) {
  padding: 8px 16px 16px;
}

/* 响应式适配 */
@media (max-width: 768px) {
  .top-toolbar {
    height: 56px;
  }
  
  .page-title {
    font-size: 18px;
    margin-left: 8px;
  }
  
  .image-indicator {
    padding: 6px 12px;
    margin-left: 12px;
  }
  
  .indicator-text {
    font-size: 14px;
  }
  
  .nav-btn {
    width: 48px !important;
    height: 48px !important;
  }
  
  .prev-btn {
    left: 16px;
  }
  
  .next-btn {
    right: 16px;
  }
  
  .bottom-toolbar {
    bottom: 16px;
    padding: 10px 16px;
    gap: 2px;
  }
  
  .bottom-toolbar .v-btn {
    width: 40px !important;
    height: 40px !important;
  }
  
  .preview-image {
    max-width: 95vw;
    max-height: calc(100vh - 160px);
  }
}
</style>

